import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MailX,
  ExternalLink,
  Clock,
  CalendarPlus,
  CheckCircle,
  AlertTriangle,
  Send,
  X,
} from 'lucide-react';

/** Per-publication tone classes — mirrors ArticlesPage.siteToneClass so
 *  the operator's eye reads the badge colour the same way across pages.
 *  Returns a "sent" (full-strength) variant and a "scheduled" (grey)
 *  variant; the grey variant is the same across publications because
 *  the message is "queued, not actioned yet". */
function publicationTone(targetName: string): { sent: string; pretty: string } {
  const t = targetName.toLowerCase();
  if (t === 'orecast') return { sent: 'bg-amber-100 text-amber-700 border-amber-200', pretty: 'Orecast' };
  if (t === 'bellwire') return { sent: 'bg-sky-100 text-sky-700 border-sky-200', pretty: 'Bell Wire' };
  if (t === 'marketfloor') return { sent: 'bg-emerald-100 text-emerald-700 border-emerald-200', pretty: 'The Market Floor' };
  if (t === 'thepick') return { sent: 'bg-purple-100 text-purple-700 border-purple-200', pretty: 'The Pick' };
  return { sent: 'bg-grey-100 text-grey-700 border-grey-200', pretty: targetName };
}

const SCHEDULED_TONE = 'bg-grey-100 text-grey-500 border-grey-200';
import {
  DraftSummary,
  EntityDayGroupRow,
  FinalRecipient,
  approveAndSendDraft,
  scheduleDraft,
  unscheduleDraft,
} from '../../lib/api/notifications.js';
import {
  ScheduleSendPopover,
  tomorrowAtSydney,
  formatSydneyShort,
} from './ScheduleSendPopover.js';
import { CoverageBadges } from '../CoverageBadges.js';

interface EntityDayGroupProps {
  group: EntityDayGroupRow;
  /** Called after any successful mutation so the parent can invalidate
   *  the entity-day query + the scheduled-calendar query. */
  onMutated: () => void;
  /** Open the existing DraftDrawer for full editing. */
  onOpenDraft: (draftId: string) => void;
}

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  pending_approval: { label: 'Pending', cls: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-blue-50 text-blue-700' },
  scheduled: { label: 'Scheduled', cls: 'bg-purple-50 text-purple-700' },
  sent: { label: 'Sent', cls: 'bg-success-50 text-success-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-grey-100 text-grey-500' },
  rejected: { label: 'Rejected', cls: 'bg-grey-100 text-grey-500' },
  expired: { label: 'Expired', cls: 'bg-grey-100 text-grey-500' },
};

export function EntityDayGroup({ group, onMutated, onOpenDraft }: EntityDayGroupProps) {
  const [expanded, setExpanded] = useState(true);

  const ticker = group.entity_identifier;
  const name = group.entity_display_name ?? 'Unknown entity';
  const isSitewide = group.entity_id === null;

  // Surface "no email contacts" at the group level so the operator can
  // deprioritise these entities at a glance and come back to source
  // contacts later. Test: does the draft carry at least one recipient row
  // with a usable email address? Inclusion verdict (approved / role not in
  // scope / suppressed / etc.) is a SEPARATE concern — operator wants to
  // know "is there any email present for this article at all?" so they can
  // mark the entity as "needs contact sourcing".
  const draftHasAnyEmail = (d: typeof group.drafts[number]) =>
    (d.recipients ?? []).some((r) => typeof r.email === 'string' && r.email.trim().length > 0);
  const noContactCount = group.drafts.filter((d) => !draftHasAnyEmail(d)).length;
  const allMissingContacts = noContactCount > 0 && noContactCount === group.drafts.length;

  return (
    <section className="rounded-lg border border-grey-200 bg-surface shadow-sm">
      {/* Group header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-grey-50 rounded-t-lg"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-grey-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-grey-500 flex-shrink-0" />
          )}
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className={`text-sm font-semibold truncate ${isSitewide ? 'text-grey-500 italic' : 'text-grey-900'}`}>
              {name}
            </span>
            {ticker && (
              <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-grey-100 text-grey-700 flex-shrink-0">
                {ticker}
              </span>
            )}
            <CoverageBadges tiers={group.coverage_tiers} />
            {/* "No contacts" badge — deprioritises the group at a glance. */}
            {allMissingContacts && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded bg-warning-50 text-warning-700 border border-warning-200 flex-shrink-0"
                title="No usable email contacts on any draft in this group. Sourceable later from the entity's Contacts."
              >
                <MailX className="h-3 w-3" />
                No contacts
              </span>
            )}
            {!allMissingContacts && noContactCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded bg-grey-50 text-grey-700 border border-grey-200 flex-shrink-0"
                title={`${noContactCount} of ${group.drafts.length} drafts in this group have no usable email contacts.`}
              >
                <MailX className="h-3 w-3" />
                {noContactCount} without contacts
              </span>
            )}
            {/* Per-publication status overlay: brand-coloured "sent"
                badges + neutral-grey "scheduled" badges. */}
            {group.sent_targets.map((tn) => {
              const tone = publicationTone(tn);
              return (
                <span
                  key={`sent-${tn}`}
                  className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border flex-shrink-0 ${tone.sent}`}
                  title={`Already sent to ${tone.pretty} for this entity-day.`}
                >
                  <CheckCircle className="h-3 w-3" />
                  {tone.pretty}
                </span>
              );
            })}
            {group.scheduled_targets.map((tn) => {
              const tone = publicationTone(tn);
              return (
                <span
                  key={`scheduled-${tn}`}
                  className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border flex-shrink-0 ${SCHEDULED_TONE}`}
                  title={`Queued for ${tone.pretty}.`}
                >
                  <Clock className="h-3 w-3" />
                  {tone.pretty}
                </span>
              );
            })}
          </div>
        </div>
        <span className="text-xs text-grey-500 flex-shrink-0">
          {group.drafts.length} draft{group.drafts.length === 1 ? '' : 's'}
        </span>
      </button>

      {/* Group body */}
      {expanded && (
        <div className="border-t border-grey-200 divide-y divide-grey-100">
          {group.drafts.map((draft) => (
            <DraftRow
              key={draft.draft_id}
              draft={draft}
              onMutated={onMutated}
              onOpen={() => onOpenDraft(draft.draft_id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface DraftRowProps {
  draft: DraftSummary;
  onMutated: () => void;
  onOpen: () => void;
}

function DraftRow({ draft, onMutated, onOpen }: DraftRowProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const status = draft.status;
  const isActionable = status === 'pending_approval' || status === 'approved';
  const isScheduled = status === 'scheduled';

  const statusMeta = STATUS_PILL[status] ?? STATUS_PILL.pending_approval;
  const recipientsFinal: FinalRecipient[] = (draft.recipients ?? [])
    .filter((r) => r.verdict === 'included')
    .map((r) => ({ email: r.email, included: true }));

  const sendNow = async () => {
    if (!recipientsFinal.length) {
      setError('No included recipients on this draft. Open the drawer to review.');
      return;
    }
    setBusy('send'); setError(null);
    try {
      await approveAndSendDraft(draft.draft_id, { recipients_final: recipientsFinal });
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to send');
    } finally {
      setBusy(null);
    }
  };

  const quickSchedule = async (hourSydney: number, label: string) => {
    if (!recipientsFinal.length) {
      setError('No included recipients on this draft. Open the drawer to review.');
      return;
    }
    setBusy(label); setError(null);
    try {
      const scheduledFor = tomorrowAtSydney(hourSydney);
      await scheduleDraft(draft.draft_id, {
        recipients_final: recipientsFinal,
        scheduled_for: scheduledFor,
      });
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to schedule');
    } finally {
      setBusy(null);
    }
  };

  const submitSchedule = async (utcIso: string) => {
    if (!recipientsFinal.length) {
      setError('No included recipients on this draft. Open the drawer to review.');
      return;
    }
    setBusy('schedule'); setError(null);
    try {
      await scheduleDraft(draft.draft_id, {
        recipients_final: recipientsFinal,
        scheduled_for: utcIso,
      });
      setPickerOpen(false);
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to schedule');
    } finally {
      setBusy(null);
    }
  };

  const handleUnschedule = async () => {
    setBusy('unschedule'); setError(null);
    try {
      await unscheduleDraft(draft.draft_id);
      onMutated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to unschedule');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-4 py-3">
      {/* Row identifier line */}
      <div className="flex items-start gap-2">
        <Mail className="h-4 w-4 text-grey-400 mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 text-xs text-grey-600 flex-wrap">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${statusMeta.cls}`}>
              {statusMeta.label}
            </span>
            {draft.entity_identifier && (
              <span className="font-mono text-[11px] text-grey-700">{draft.entity_identifier}</span>
            )}
            <span className="text-grey-400">·</span>
            <span className="font-medium text-grey-700">{draft.target_name}</span>
            {draft.target_test_mode_enabled && isActionable && (
              <span className="text-warning-700 font-medium">· TEST MODE</span>
            )}
          </div>
          <button
            type="button"
            onClick={onOpen}
            className="text-left text-sm font-medium text-grey-900 hover:text-primary-04 truncate block w-full"
            title={draft.article_title ?? undefined}
          >
            {draft.article_title || draft.rendered_subject}
          </button>
          <div className="mt-1 text-xs text-grey-500 flex items-center gap-3 flex-wrap">
            {/* Article date — per feedback_article_date_resolution, live-mode
                article date = generation_timestamp. Falls back to draft.created_at
                only if the article row is missing one. */}
            {(draft.article_generation_timestamp || draft.created_at) && (
              <span className="tabular-nums" title="Article generated at (Sydney)">
                {formatSydneyShort(draft.article_generation_timestamp || draft.created_at)}
              </span>
            )}
            <span className="tabular-nums">· {draft.included_count} included</span>
            {draft.excluded_count > 0 && (
              <span className="tabular-nums">· {draft.excluded_count} excluded</span>
            )}
            {draft.live_url && (
              <a
                href={draft.live_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-primary-04 hover:text-primary-600 font-medium"
              >
                View article <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {isScheduled && draft.scheduled_for && (
              <span className="inline-flex items-center gap-1 text-purple-700 font-medium">
                <Clock className="h-3 w-3" />
                {formatSydneyShort(draft.scheduled_for)}
                {draft.scheduled_by && (
                  <span className="text-grey-500 font-normal">· by {draft.scheduled_by}</span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action panel */}
      {(isActionable || isScheduled) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6 relative">
          {isActionable && (
            <>
              <button
                onClick={sendNow}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary-04 text-white rounded hover:bg-primary-600 disabled:opacity-50"
              >
                <Send className="h-3 w-3" />
                {busy === 'send' ? 'Sending…' : 'Send now'}
              </button>
              <button
                onClick={() => quickSchedule(10, '10am')}
                disabled={!!busy}
                className="px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
              >
                {busy === '10am' ? '…' : 'Tomorrow 10am'}
              </button>
              <button
                onClick={() => quickSchedule(14, '2pm')}
                disabled={!!busy}
                className="px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
              >
                {busy === '2pm' ? '…' : 'Tomorrow 2pm'}
              </button>
              <button
                onClick={() => setPickerOpen((v) => !v)}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
              >
                <CalendarPlus className="h-3 w-3" />
                Schedule…
              </button>
            </>
          )}
          {isScheduled && (
            <>
              <button
                onClick={() => setPickerOpen((v) => !v)}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
              >
                <Clock className="h-3 w-3" />
                Edit time
              </button>
              <button
                onClick={handleUnschedule}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-600 border border-danger-200 rounded hover:bg-danger-50 disabled:opacity-50"
              >
                <X className="h-3 w-3" />
                {busy === 'unschedule' ? '…' : 'Unschedule'}
              </button>
            </>
          )}

          {pickerOpen && (
            <ScheduleSendPopover
              initialUtcIso={isScheduled ? draft.scheduled_for : null}
              expiresAtUtcIso={draft.expires_at}
              submitting={busy === 'schedule'}
              onSubmit={submitSchedule}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 ml-6 flex items-start gap-1 text-xs text-danger-600 bg-danger-50 border border-danger-200 rounded px-2 py-1">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
